import { Link } from 'react-router-dom';
import { mediaAsset } from '../config/media';

function StudySection({ eyebrow, title, children, className = '' }) {
  return (
    <section className={`remote-study__section ${className}`.trim()}>
      <div className="remote-study__heading">
        <p className="portfolio-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <div className="remote-study__body">{children}</div>
    </section>
  );
}

export default function LullabaiCaseStudy() {
  return (
    <main id="main-content" className="portfolio-page remote-study lullabai-study">
      <header className="portfolio-page-intro remote-study__intro">
        <p className="portfolio-eyebrow">Co-founder · Generative music · iOS</p>
        <h1>Lullabai</h1>
        <p className="lullabai-study__tagline">Mindfulness for music lovers.</p>
        <div className="portfolio-actions">
          <a className="portfolio-text-link" href="https://lullabai.live/" target="_blank" rel="noreferrer">
            Visit lullabai.live <span aria-hidden="true">↗</span>
          </a>
        </div>
      </header>

      <section className="case-study-summary remote-study__summary" aria-label="Lullabai project summary">
        <div>
          <p className="portfolio-eyebrow">Role</p>
          <p>Co-founder, Product Designer, Audio Systems Designer, and Mobile Engineer</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Period</p>
          <p>2020–Present</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Focus</p>
          <p>Generative music, React Native, audio-production automation, and product design</p>
        </div>
      </section>

      <figure className="case-study-hero lullabai-study__hero">
        <div className="lullabai-study__brand">
          <img
            className="lullabai-study__app-icon"
            src={mediaAsset('images/lullabai/lullabai-app-icon.png')}
            alt=""
            aria-hidden="true"
          />
          <p>Original generative music organized into channels for focus, rest, and sleep.</p>
        </div>
        <img
          className="lullabai-study__screen"
          src={mediaAsset('images/lullabai/lullabai-channel-browser.webp')}
          alt="Lullabai iOS channel browser showing Gentle Guitar, Peaceful Piano, Rainbow Noise, Nocturne, and Exosphere"
        />
        <figcaption>Lullabai’s iOS channel browser.</figcaption>
      </figure>

      <section className="remote-study__metrics" aria-label="Selected Lullabai outcomes">
        <article><strong>20+</strong><span>Original listening channels</span></article>
        <article><strong>Days</strong><span>Of finished audio generated per batch</span></article>
        <article><strong>100%</strong><span>Original composition training data</span></article>
        <article><strong>iOS</strong><span>Product launched on the App Store</span></article>
      </section>

      <StudySection eyebrow="Context" title="Familiar music without the repetition">
        <p>
          Lullabai grew out of my long creative collaboration with Ben Brodie. We had originally developed the technology for <cite>Lonely Computers on the Internet</cite>, a musical comedy about artificial intelligence. When that stage project was put on hold during the 2020 lockdown, we redirected the work into a mindfulness product for mental health and wellness.
        </p>
        <p>
          Relaxation music can become distracting when a listener learns every transition in a repeated track or playlist. We wanted to preserve the familiarity of a musical style without returning to the same recording with every listen.
        </p>
        <p>
          Lullabai organized the experience into more than 20 channels for sleep, meditation, yoga, and relaxation. Instead of selecting songs, listeners chose an atmosphere such as Peaceful Piano or Soothing Strings and entered an effectively inexhaustible stream of music created for that sound world. The pool was not technically infinite, but it contained more music than a listener could realistically hear and retain, making the stream feel infinite in practice.
        </p>
        <p>
          I led sound design and frontend development, including the audio-generation and production pipeline and the React Native application. Ben designed the language-model architecture and Icecast backend, and composed all of the music used to establish the foundation model. We expanded that original training corpus with additional compositions by me and original works we commissioned from other artists.
        </p>
      </StudySection>

      <StudySection eyebrow="Research" title="Building the dataset in-house" className="remote-study__section--dark">
        <p>
          This began in 2020, before generative audio became a familiar consumer category. As musicians, we did not want to train the model with work taken from other artists without their explicit consent. We assembled an in-house dataset from Ben's foundation compositions, additional pieces I contributed, and original works commissioned from other artists. We converted that material through a notation-to-MIDI pipeline into training data for a custom language model that generated symbolic scores.
        </p>
        <p>
          The project continued questions I explored in my 2020 MFA thesis, <a href="https://mtiid.calarts.edu/wp-content/uploads/2020/06/MTEC690_Thesis_Danner3.1.pdf" target="_blank" rel="noreferrer"><cite>smArt: An Analytical Framework for Artistic Applications of Autonomous and Semi-Autonomous Systems</cite></a>, about authorship, agency, and creative intent when artists work with autonomous systems.
        </p>
        <p>
          The first milestone was that the model could produce coherent, relaxing compositions. As artists, we wanted to know whether harmony, melodic shape, pacing, repetition, and development would remain recognizable as part of the source composer's artistic style after the music had been represented as notation and MIDI, learned by the model, and generated as a new score.
        </p>
        <p>
          We shaped the material around long-form stability, restrained development, and enough variation to avoid obvious repetition without interrupting sleep or focused activity. Owning the dataset gave it a clear creative origin and let us evaluate the output against the source compositions we created and commissioned.
        </p>
        <figure className="lullabai-study__wordmark-panel">
          <img src={mediaAsset('images/lullabai/lullabai-logo.webp')} alt="Lullabai" loading="lazy" />
        </figure>
      </StudySection>

      <StudySection eyebrow="Audio systems" title="From generated scores to finished audio">
        <p>
          The model produced musical information, not finished recordings. My main technical and creative challenge was turning those scores into audio that sounded intentionally produced rather than mechanically rendered.
        </p>
        <p>
          I built production templates in Logic Pro, Ableton Live, and REAPER using samplers, synthesizers, convolution reverb, vocoding, and JavaScript MIDI processors. The processors introduced controlled variation in timing, velocity, articulation, arrangement, mixing, and effects so repeated renders did not share the same balance or texture.
        </p>
        <p>
          I also captured field recordings at Kaʻena Point in Hawaiʻi and in forests in the Catskills. I edited and mapped those recordings into custom sampler instruments that the generated scores could play through the DAW templates. The recordings became musical material rather than a background ambience, giving individual channels a stronger sense of place.
        </p>
        <p>
          Python, FFmpeg, and mp3splt tools processed the long renders, rejected unsuitable MIDI, split batches at silence, normalized and converted the audio, and applied consistent fades. The complete system could turn model output into days of finished music in one run.
        </p>
        <figure className="lullabai-study__pipeline-image">
          <img
            src={mediaAsset('images/lullabai/lullabai-audio-pipeline.webp')}
            alt="Pipeline from original musical material through algorithmic processing to finished audio"
            loading="lazy"
          />
          <figcaption>MIDI and source material passed through the generative and production system to create finished audio.</figcaption>
        </figure>
      </StudySection>

      <StudySection eyebrow="Product engineering" title="A listening experience designed to disappear" className="remote-study__split">
        <div>
          <p>
            Each channel had its own catalog. When a listener connected, the backend selected music from that pool and delivered it through Icecast. The result felt closer to tuning into a purpose-built radio station than managing an album or playlist.
          </p>
          <p>
            I designed and built the React Native app with remotely configured channels, browsing, search, recent listening, and persistent playback. It supported background audio, lock-screen controls, AirPlay, audio interruptions, and a sleep timer that gradually lowered the volume before stopping.
          </p>
          <p>
            We tested the product in a New York City classroom, where it was used as background music during reading time. That trial showed the listening model could support a calm environment outside its original sleep-focused context.
          </p>
        </div>
        <div className="lullabai-study__video">
          <iframe
            src="https://www.youtube-nocookie.com/embed/KONeAU7nXbc"
            title="Lullabai product and generative music demonstration"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </StudySection>

      <StudySection eyebrow="Product strategy" title="Responding to a changing market" className="remote-study__section--accent">
        <p>
          Generative-audio products changed quickly after launch. Full audio generation became widely available, while relaxation music remained an exceptionally crowded category. Distribution and customer acquisition became more restrictive than our ability to generate high-quality music at scale.
        </p>
        <p>
          We explored sync licensing as a stronger commercial direction. I built a browser prototype where a user could select tempo, tonic, and mode, generate a MIDI composition, audition it through a Tone.js sampled piano with animated note feedback, and download it for use with other instruments.
        </p>
        <p>
          We also prototyped artist-specific models using music supplied by participating artists. The technology worked, but we chose not to scale it while balancing other commitments and questioning whether further engineering would produce a defensible business.
        </p>
        <figure className="lullabai-study__api-image">
          <img
            src={mediaAsset('images/lullabai/lullabai-api.jpeg')}
            alt="Lullabai API identity with the spiral cloud mark"
            loading="lazy"
          />
          <figcaption>The Lullabai API concept reflected the shift toward licensing generated material.</figcaption>
        </figure>
      </StudySection>

      <StudySection eyebrow="Reflection" title="Composition as a product" className="remote-study__closing">
        <h3>Emerging technology rewards a different risk appetite</h3>
        <p>
          Some companies moved quickly by using other artists' work first and addressing consent later. We were not comfortable taking that approach, and we did not have the institutional backing to absorb the legal exposure. I still believe our decision was right. It taught me that ethics, access to capital, and risk tolerance can shape a new market as much as technical capability.
        </p>
        <h3>More music did not create more consumer value</h3>
        <p>
          File sharing and streaming had already made recorded music feel abundant and inexpensive. AI increased the supply, but listeners already had access to more music than they could consume. The credible monetization opportunity was sync licensing, where a buyer pays for music that meets a defined production need and carries clear usage rights—not the consumer relaxation market we initially targeted.
        </p>
        <h3>The value of consumer media lies beyond utility</h3>
        <p>
          We solved difficult engineering problems and built a cohesive product. The more important question should have come first: how strongly did customers need it, and how poorly did existing products serve them? Streaming services had already met most demand for access and discovery. What listeners could value was our artistic curation—the compositions, production choices, field recordings, channel concepts, and judgment—not limitlessness by itself.
        </p>
        <p>
          At launch, we focused on feasibility: could the system produce music that listeners would accept as coherent, intentional, and emotionally appropriate rather than as an uncanny imitation? Our clearest differentiator was the curatorial approach across dataset development, training, model design, and audio rendering. That process preserved recognizable aspects of the source composer's style and remains the most promising direction for future development, distinguishing Lullabai from products built around scale or generic output.
        </p>
        <aside className="remote-study__boundary">
          <p className="portfolio-eyebrow">Attribution</p>
          <p>
            I owned the product concept, audio-production system, DAW templates, MIDI automation, sound design, field-recording instruments, batch processing, and React Native experience. My founding partner designed the language model and Icecast backend. We shared composition and overall product development.
          </p>
        </aside>
        <div className="portfolio-actions">
          <Link className="portfolio-button" to="/contact">Start a conversation</Link>
          <Link className="portfolio-text-link" to="/work">Return to selected work</Link>
        </div>
      </StudySection>
    </main>
  );
}
